/**
 * useDiligenciaPermissions — Hook de permissões para admins de diligência.
 *
 * Determina o que um admin pode ver/fazer baseado na sua especialidade.
 * Admin principal (especialidade = null) tem acesso total.
 * Admins especializados veem apenas suas etapas.
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext.hook';
import {
  type DiligenciaEtapa,
  type DiligenciaEspecialidade,
  ETAPAS_ORDENADAS,
  ESPECIALIDADE_ETAPAS,
} from '@/utils/diligenciaChecklists';

export interface DiligenciaPermissions {
  /** Especialidade do admin (null = admin principal) */
  especialidade: DiligenciaEspecialidade | null;
  /** Etapas que este admin pode acessar */
  etapasPermitidas: DiligenciaEtapa[];
  /** Admin principal com acesso total */
  isAdminPrincipal: boolean;
  /** Admin com especialidade definida */
  isAdminEspecializado: boolean;
  /** Verifica se o admin pode acessar uma etapa específica */
  canAccessEtapa: (etapa: DiligenciaEtapa) => boolean;
  /** Pode avançar/retroceder etapas (somente admin principal) */
  canAdvanceRetreat: boolean;
  /** Pode aprovar/reprovar diligência (somente admin principal) */
  canApproveReject: boolean;
}

/**
 * Valida se uma string é uma especialidade válida.
 */
function isValidEspecialidade(value: string | null): value is DiligenciaEspecialidade {
  if (!value) return false;
  return value === 'juridico' || value === 'tecnica_economica' || value === 'contabil';
}

export function useDiligenciaPermissions(): DiligenciaPermissions {
  const { profile, isAdmin, isDiligenciasManager } = useAuth();

  return useMemo(() => {
    const rawEspecialidade = profile?.diligencia_especialidade ?? null;
    const especialidade = isValidEspecialidade(rawEspecialidade) ? rawEspecialidade : null;

    // diligencias_manager opera como admin principal dentro do escopo de diligências.
    // Não tem especialidade — vê todas as etapas e pode avançar/retroceder/aprovar/rejeitar.
    const isPrincipalLike = (isAdmin && !especialidade) || isDiligenciasManager;
    const isAdminPrincipal = isPrincipalLike;
    const isAdminEspecializado = isAdmin && !!especialidade;

    const etapasPermitidas: DiligenciaEtapa[] = especialidade
      ? (ESPECIALIDADE_ETAPAS[especialidade] ?? [...ETAPAS_ORDENADAS])
      : [...ETAPAS_ORDENADAS];

    const etapasSet = new Set(etapasPermitidas);

    return {
      especialidade,
      etapasPermitidas,
      isAdminPrincipal,
      isAdminEspecializado,
      canAccessEtapa: (etapa: DiligenciaEtapa) => etapasSet.has(etapa),
      canAdvanceRetreat: isPrincipalLike,
      canApproveReject: isPrincipalLike,
    };
  }, [profile?.diligencia_especialidade, isAdmin, isDiligenciasManager]);
}
